import { useState, useEffect } from "react";
import api from '../api/axios';
import { useNavigate } from "react-router-dom";
import { FaDna, FaCheck, FaTimes } from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useTranslation } from "../context/LanguageContext";
import "../styles/common.css";
import "./AjouterReproduction.css";

export default function AjouterReproduction() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    date_chaleur: "",
    date_saillie: "",
    date_velage: "",
    nb_veaux: 1,
    statut: "en_cours",
    notes: "",
    mere: "",
    pere: "",
  });
  const [animaux, setAnimaux] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({
    mere: "",
    date_chaleur: "",
  });
  const [touchedFields, setTouchedFields] = useState({
    mere: false,
    date_chaleur: false,
  });
  const navigate = useNavigate();

  const STATUT_CHOICES = React.useMemo(() => [
    { value: "en_cours", label: t.statusInProgress },
    { value: "termine", label: t.statusCompleted },
    { value: "echec", label: t.statusFailed },
  ], [t]);

  useEffect(() => {
    const fetchAnimaux = async () => {
      try {
        const res = await api.get("/api/animaux/");
        setAnimaux(res.data);
        setError(null);
      } catch (err) {
        setError("Erreur lors du chargement des animaux.");
      } finally {
        setLoading(false);
      }
    };
    fetchAnimaux();
  }, []);

  // Fonction de validation des champs obligatoires
  const validateRequiredField = (name, value) => {
    if (!value || (typeof value === 'string' && value.trim() === "") || value === null) {
      return t.fieldRequired;
    }
    return "";
  };

  // Validation d'un champ spécifique
  const validateField = (name, value) => {
    if (name === "mere" || name === "date_chaleur") {
      return validateRequiredField(name, value);
    }
    return "";
  };

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setFormData({
      ...formData,
      [name]: type === "number" ? Number(value) : value,
    });
    
    // Validation en temps réel si le champ a été touché
    if (touchedFields[name] || fieldErrors[name]) {
      const error = validateField(name, value);
      setFieldErrors(prev => ({
        ...prev,
        [name]: error
      }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    
    // Marquer le champ comme touché
    setTouchedFields(prev => ({ ...prev, [name]: true }));
    
    // Valider le champ
    const error = validateField(name, value);
    setFieldErrors(prev => ({
      ...prev,
      [name]: error
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Marquer tous les champs obligatoires comme touchés
    const newTouchedFields = {
      mere: true,
      date_chaleur: true,
    };
    setTouchedFields(newTouchedFields);
    
    // Valider tous les champs obligatoires
    const newFieldErrors = {
      mere: validateField("mere", formData.mere),
      date_chaleur: validateField("date_chaleur", formData.date_chaleur),
    };
    setFieldErrors(newFieldErrors);
    
    // Vérifier s'il y a des erreurs
    const hasErrors = Object.values(newFieldErrors).some(error => error !== "");
    
    if (hasErrors) {
      return; // Ne pas soumettre si il y a des erreurs
    }
    
    setShowConfirmModal(true);
  };

  const handleConfirmSubmit = async () => {
    setSubmitting(true);
    try {
      await api.post("/api/reproductions/", formData);
      setShowConfirmModal(false);
      toast.success("Reproduction ajoutée avec succès !");
      setTimeout(() => {
        navigate("/reproductions");
      }, 500);
    } catch (err) {
      console.error("Erreur lors de l'enregistrement:", err);
      const errorMessage = err.response?.data?.detail || 
                          err.response?.data?.message ||
                          Object.values(err.response?.data || {}).flat().join(", ") ||
                          "Une erreur s'est produite lors de l'enregistrement.";
      toast.error(errorMessage);
      setShowConfirmModal(false);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelConfirm = () => {
    setShowConfirmModal(false);
  };

  const isFormValid = () => {
    if (!formData.mere || !formData.mere.trim()) return false;
    if (!formData.date_chaleur || !formData.date_chaleur.trim()) return false;
    return true;
  };

  const getAnimalMatricule = (animalId) => {
    if (!animalId) return t.notSelected;
    const animal = animaux.find(a => a.id === parseInt(animalId));
    return animal ? animal.matricule : t.notSelected;
  };

  const getStatutLabel = (statut) => {
    const choice = STATUT_CHOICES.find(c => c.value === statut);
    return choice ? choice.label : statut;
  };

  if (loading) {
    return (
      <div className="ajouter-reproduction-page">
        <div className="loading-container">
          <p className="loading-text">{t.loading}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ajouter-reproduction-page">
        <div className="title-badge">
          <FaDna className="page-title-icon" aria-hidden="true" />
          <span>{t.error}</span>
        </div>
        <div className="error-container">
          <p className="error-message">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="ajouter-reproduction-page">
      <div className="title-badge">
        <FaDna className="page-title-icon" aria-hidden="true" />
        <span>{t.addReproductionTitle}</span>
      </div>

      <form onSubmit={handleSubmit} className="ajouter-reproduction-form">
        {/* Bannière d'information */}
        <div className="form-info-banner">
          <span className="info-icon">ℹ️</span>
          <div className="info-content">
            <strong>{t.requiredFields}</strong>
            <span className="required-indicator">*</span> {t.requiredFieldNote}
          </div>
        </div>

        <div className="form-section-container">
          <div className="form">
            <label htmlFor="mere">
              {t.damLabel} <span className="required-indicator">*</span>
            </label>
            <select 
              id="mere"
              name="mere" 
              value={formData.mere || ""} 
              onChange={handleChange}
              onBlur={handleBlur}
              className={fieldErrors.mere && touchedFields.mere ? "input-error" : ""}
            >
              <option value="">-- Sélectionner --</option>
              {animaux
                .filter(animal => animal.sexe === "F")
                .map(animal => (
                  <option key={animal.id} value={animal.id}>
                    {animal.matricule}
                  </option>
                ))}
            </select>
            {fieldErrors.mere && touchedFields.mere && (
              <span className="field-error-message">{fieldErrors.mere}</span>
            )}
          </div>

          <div className="form">
            <label htmlFor="pere">{t.sireLabel}</label>
            <select 
              id="pere"
              name="pere" 
              value={formData.pere || ""} 
              onChange={handleChange}
            >
              <option value="">-- Sélectionner (optionnel) --</option>
              {animaux
                .filter(animal => animal.sexe === "M")
                .map(animal => (
                  <option key={animal.id} value={animal.id}>
                    {animal.matricule}
                  </option>
                ))}
            </select>
          </div>

          <div className="form">
            <label htmlFor="date_chaleur">
              {t.heatDate} <span className="required-indicator">*</span>
            </label>
            <input 
              type="date" 
              id="date_chaleur"
              name="date_chaleur" 
              value={formData.date_chaleur || ""} 
              onChange={handleChange}
              onBlur={handleBlur}
              className={fieldErrors.date_chaleur && touchedFields.date_chaleur ? "input-error" : ""}
            />
            {fieldErrors.date_chaleur && touchedFields.date_chaleur && (
              <span className="field-error-message">{fieldErrors.date_chaleur}</span>
            )}
          </div>

          <div className="form">
            <label htmlFor="date_saillie">{t.breedingDate}</label>
            <input 
              type="date" 
              id="date_saillie"
              name="date_saillie" 
              value={formData.date_saillie || ""} 
              onChange={handleChange}
            />
          </div>

          <div className="form">
            <label htmlFor="statut">{t.statusLabel}</label>
            <select 
              id="statut"
              name="statut" 
              value={formData.statut} 
              onChange={handleChange}
            >
              {STATUT_CHOICES.map(choice => (
                <option key={choice.value} value={choice.value}>
                  {choice.label}
                </option>
              ))}
            </select>
          </div>

          <div className="form">
            <label htmlFor="date_velage">{t.calvingDate}</label>
            <input 
              type="date" 
              id="date_velage"
              name="date_velage" 
              value={formData.date_velage || ""} 
              onChange={handleChange}
            />
          </div>

          <div className="form">
            <label htmlFor="nb_veaux">{t.calvesNumber}</label>
            <input 
              type="number" 
              id="nb_veaux"
              name="nb_veaux" 
              value={formData.nb_veaux} 
              onChange={handleChange} 
              min="0"
            />
          </div>

          <div className="form full-width">
            <label htmlFor="notes">{t.notes}</label>
            <textarea 
              id="notes"
              name="notes" 
              value={formData.notes || ""} 
              onChange={handleChange}
              placeholder={t.reproductionNotes}
              rows="3"
            />
          </div>
        </div>

        {/* Boutons d'action */}
        <div className="actions">
          <button
            type="submit"
            className="save-but"
            disabled={!isFormValid() || submitting}
          >
            <FaCheck />
            <span>{submitting ? t.registering : t.register}</span>
          </button>
          <button
            type="button"
            className="cancel-but"
            onClick={() => navigate("/reproductions")}
            disabled={submitting}
          >
            <FaTimes />
            <span>{t.cancel}</span>
          </button>
        </div>
      </form>

      {/* Modal de confirmation */}
      {showConfirmModal && (
        <div className="confirm-modal-overlay" onClick={handleCancelConfirm}>
          <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="confirm-modal-header">
              <FaCheck className="confirm-modal-icon" />
              <h3>{t.confirmAdd}</h3>
            </div>
            <div className="confirm-modal-body">
              <p className="confirm-intro">
                {t.verifyInfoBeforeConfirm}
              </p>
              <div className="confirm-summary">
                <div className="summary-row">
                  <span className="summary-label">{t.summaryDam}</span>
                  <span className="summary-value">{getAnimalMatricule(formData.mere) || t.notProvided}</span>
                </div>
                {formData.pere && (
                  <div className="summary-row">
                    <span className="summary-label">{t.summarySire}</span>
                    <span className="summary-value">{getAnimalMatricule(formData.pere)}</span>
                  </div>
                )}
                {formData.date_chaleur && (
                  <div className="summary-row">
                    <span className="summary-label">{t.summaryHeatDate}</span>
                    <span className="summary-value">{formData.date_chaleur}</span>
                  </div>
                )}
                {formData.date_saillie && (
                  <div className="summary-row">
                    <span className="summary-label">{t.summaryBreedingDate}</span>
                    <span className="summary-value">{formData.date_saillie}</span>
                  </div>
                )}
                <div className="summary-row">
                  <span className="summary-label">{t.summaryStatus}</span>
                  <span className="summary-value">{getStatutLabel(formData.statut)}</span>
                </div>
                {formData.date_velage && (
                  <div className="summary-row">
                    <span className="summary-label">{t.summaryCalvingDate}</span>
                    <span className="summary-value">{formData.date_velage}</span>
                  </div>
                )}
                <div className="summary-row">
                  <span className="summary-label">{t.summaryCalvesNumber}</span>
                  <span className="summary-value">{formData.nb_veaux}</span>
                </div>
                {formData.notes && (
                  <div className="summary-row">
                    <span className="summary-label">{t.notes}:</span>
                    <span className="summary-value">{formData.notes}</span>
                  </div>
                )}
              </div>
            </div>
            <div className="confirm-modal-footer">
              <button
                type="button"
                onClick={handleCancelConfirm}
                className="confirm-modal-cancel"
                disabled={submitting}
              >
                <FaTimes />
                <span>{t.cancel}</span>
              </button>
              <button
                type="button"
                onClick={handleConfirmSubmit}
                className="confirm-modal-confirm"
                disabled={submitting}
              >
                <FaCheck />
                <span>{submitting ? t.registering : t.confirm}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </div>
  );
}
